import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styles from './toggle_switch.module.css';

export interface ToggleOption<T extends string> {
  value: T;
  label: string;
  color?: string;
  ariaLabel?: string;
}

interface ToggleSwitchProps<T extends string> {
  value?: T;
  options: readonly ToggleOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  equalWidth?: boolean;
  width?: string;
  height?: string;
  size?: 'medium' | 'small';
}

export function ToggleSwitch<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  equalWidth = false,
  width = 'auto',
  height = '40px',
  size = 'medium',
}: Readonly<ToggleSwitchProps<T>>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  const activeIndex = value === undefined || options.length === 0 ? -1 : options.findIndex((o) => o.value === value);
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;
  const hasActiveOption = activeIndex >= 0;

  const handleClick = useCallback(
    (optionValue: T) => {
      if (value !== optionValue) onChange(optionValue);
    },
    [onChange, value]
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!hasActiveOption) {
      setSliderStyle((prev) => ({
        left: container.offsetWidth,
        width: prev.width || 0,
      }));
      return;
    }

    const buttons = container.querySelectorAll('button');
    const activeBtn = buttons[activeIndex] as HTMLButtonElement | undefined;
    if (!activeBtn) return;

    setSliderStyle({
      left: activeBtn.offsetLeft,
      width: activeBtn.offsetWidth,
    });
  }, [activeIndex, hasActiveOption]);

  return (
    <div
      ref={containerRef}
      className={styles.container}
      role="group"
      aria-label={ariaLabel}
      data-has-active={hasActiveOption}
      data-size={size}
      data-equal-width={equalWidth || undefined}
      style={
        {
          '--toggle-active-left': `${sliderStyle.left}px`,
          '--toggle-active-width': `${sliderStyle.width}px`,
          '--toggle-active-color': activeOption?.color ?? 'var(--mui-palette-primary-main)',
          width: width ?? 'auto',
          height: height ?? 'auto',
        } as React.CSSProperties
      }
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleClick(option.value)}
          className={`${styles.button} ${value === option.value ? styles.selected : ''}`}
          aria-pressed={value === option.value}
          aria-label={option.ariaLabel ?? option.label}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
